import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useUrl } from '@/composables/url-helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { useAppStore } from '@/store/app-store'
import {
    APIGameType,
    APIRedirect,
    GameCategoriesInfoType,
    LinkedCategory,
    ShortGameType,
    ShortGameZipType,
} from '@/types'
import { unzipShortGame, zipAPIGame } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'
import { isAPIRedirect } from '@/utils/requests/utils'

type GamePageRecommendationsZip = {
    recommended: ShortGameZipType[]
    top_playgama: ShortGameZipType[]
    related: ShortGameZipType[]
    random: ShortGameZipType[]
    series: ShortGameZipType[]
}

type GamePageRecommendations = {
    recommended: ShortGameType[]
    top_playgama: ShortGameType[]
    related: ShortGameType[]
    random: ShortGameType[]
    series: ShortGameType[]
}
export const useGamePageStore = defineStore('gamePage', () => {
    const game = ref<APIGameType | undefined>(undefined)
    const gameRedirect = ref<APIRedirect | null>(null)
    const { getGameByParam, getGameRecommendations } = useAPI()
    const gameCatInfo = ref<GameCategoriesInfoType | undefined>(undefined)
    const recommendationsRaw = ref<GamePageRecommendationsZip | undefined>(undefined)
    const linkedCategories = ref<LinkedCategory[]>()

    const recommendations = computed<GamePageRecommendations | undefined>(() => {
        if (!recommendationsRaw.value) {
            return undefined
        }
        return {
            recommended: recommendationsRaw.value.recommended.map(unzipShortGame),
            top_playgama: recommendationsRaw.value.top_playgama.map(unzipShortGame),
            related: recommendationsRaw.value.related.map(unzipShortGame),
            random: recommendationsRaw.value.random.map(unzipShortGame),
            series: recommendationsRaw.value.series.map(unzipShortGame),
        }
    })
    const loadedLocale = ref<string | null>(null)

    const appStore = useAppStore()
    const { getCategoryRoute } = useUrl()
    const { currentLocale } = useI18nAddon()

    async function setGame(param: string) {
        const result = await getGameByParam({
            param,
            locale: currentLocale.value,
            backendFlags: appStore.query?.backend_flags as string | undefined,
        })
        if (isAPIRedirect(result)) {
            game.value = undefined
            gameRedirect.value = result
            return result
        }
        loadedLocale.value = currentLocale.value
        if (result.game.gameUrlRedirectTarget) {
            appStore.setHeaders({
                'X-BFF-Redirect-Mechanism': result.game.gameUrlRedirectTarget.mechanism,
                'X-BFF-Redirect-Reason': result.game.gameUrlRedirectTarget.reason,
            })
        }
        game.value = result.game
        gameRedirect.value = null
        const pathCategories = result.pathCategories.map((cat) => ({
            ...cat,
            href: getCategoryRoute({ hru: cat.hru, type: cat.categoryType }),
        }))
        const categoriesInfo = result.categoriesInfo.map((cat) => ({
            ...cat,
            href: getCategoryRoute({ hru: cat.hru, type: cat.categoryType }),
        }))
        gameCatInfo.value = { pathCategories, categoriesInfo }

        return { game: game.value, ...gameCatInfo.value }
    }

    function setGameWithRecommendations(param: string) {
        return Promise.all([
            setGame(param),
            getGameRecommendations({
                hru: param,
                types: ['recommended', 'top_playgama', 'related', 'random', 'series', 'linked_categories'],
                platform: appStore.platform,
                locale: currentLocale.value,
            })
                .then((data) => {
                    recommendationsRaw.value = {
                        recommended: data.recommended?.map(zipAPIGame) || [],
                        top_playgama: data.top_playgama?.map(zipAPIGame) || [],
                        related: data.related?.map(zipAPIGame) || [],
                        random: data.random?.map(zipAPIGame) || [],
                        series: data.series?.map(zipAPIGame) || [],
                    }

                    linkedCategories.value = data.linked_categories?.map((cat) => ({
                        hru: cat.localizations[0]!.hru,
                        categoryType: cat.category_type,
                        title: cat.localizations[0]!.title,
                    }))
                })
                .catch(() => {
                    recommendationsRaw.value = {
                        recommended: [],
                        top_playgama: [],
                        related: [],
                        random: [],
                        series: [],
                    }
                }),
        ])
    }

    function resetGameData(): void {
        game.value = undefined
        gameRedirect.value = null
        gameCatInfo.value = undefined
        recommendationsRaw.value = undefined
        linkedCategories.value = undefined
    }

    function isDataValid(requestedHRU: string): boolean {
        return game.value?.hru === requestedHRU && currentLocale.value === loadedLocale.value
    }

    return {
        game,
        gameRedirect,
        gameCatInfo,
        // expose to capture on SSR, serialize and hydrate on the client
        recommendationsRaw,
        linkedCategories,
        recommendations,
        loadedLocale,
        setGame,
        setGameWithRecommendations,
        resetGameData,
        isDataValid,
    }
})
